// E-Mail und SMS direkt oben auf der Lead-Seite (Karte über dem Feed, wie Close). Die Eingabe selbst gehört dem
// Modul Kommunikation: `import { EmailKomposer, SmsKomposer } from "../../kommunikation"` mit den Props
// { leadId, kontakte, onClose, onGesendet? }. Geladen wird es erst beim ersten Öffnen (eigener Baustein im Bundle).
// Der Weg über import.meta.glob hält den Build grün, solange das Modul noch nicht da ist; dann erscheint ein Hinweis.
import { Component, lazy, Suspense, type ComponentType, type ReactNode } from "react";
import type { Kontakt } from "../../api";
import { Icon } from "../Icon";
import "../../styles/lead.css";

export const EMAIL = "email";
export const SMS = "sms";

export interface KomposerProps {
  leadId: string;
  kontakte: Kontakt[];
  onClose: () => void;
  onGesendet?: () => void;
  /** Vorbelegter Empfänger (nur E-Mail), z. B. nach Klick aufs Brief-Symbol einer Liste. */
  an?: string | null;
  /** Vorbelegter Kontakt (nur SMS), z. B. nach Klick aufs SMS-Symbol der Kontaktkarte: dessen Mobilnummer ist gewählt. */
  kontakt?: string | null;
}

interface KommunikationModul {
  EmailKomposer?: ComponentType<KomposerProps>;
  SmsKomposer?: ComponentType<KomposerProps>;
}

const quellen = import.meta.glob<KommunikationModul>("../../kommunikation/index.{ts,tsx}");
const modulLaden = Object.values(quellen)[0] as (() => Promise<KommunikationModul>) | undefined;

function fehlt(art: "email" | "sms"): ComponentType<KomposerProps> {
  return function Fehlt({ onClose }: KomposerProps) {
    return (
      <div className="ls-eingabe ls-komm-fehlt" role="region" aria-label={art === "email" ? "E-Mail" : "SMS"}>
        <div className="ls-eingabe__kopf">
          <span className={`ls-symbol ls-symbol--${art === "email" ? "email" : "sms"}`} aria-hidden="true">
            <Icon name={art === "email" ? "mail" : "sms"} groesse={13} />
          </span>
          <span className="ls-eingabe__titel">{art === "email" ? "E-Mail" : "SMS"}</span>
          <button type="button" className="symbolknopf symbolknopf--klein ls-eingabe__zu" title="Schließen (Esc)" aria-label="Schließen" onClick={onClose}>
            <Icon name="schliessen" groesse={14} />
          </button>
        </div>
        <p className="ls-leer ls-komm-fehlt__text">
          {art === "email" ? "E-Mails" : "SMS"} schreiben geht hier, sobald das Modul Kommunikation eingespielt ist. Bis dahin in Close.
        </p>
      </div>
    );
  };
}

const EmailLazy = lazy(async () => {
  const m = modulLaden ? await modulLaden() : {};
  return { default: m.EmailKomposer ?? fehlt("email") };
});
const SmsLazy = lazy(async () => {
  const m = modulLaden ? await modulLaden() : {};
  return { default: m.SmsKomposer ?? fehlt("sms") };
});

/** Fehler beim Laden oder in der Eingabe selbst: Karte mit Hinweis statt weißer Seite. */
class Grenze extends Component<{ children: ReactNode; onClose: () => void }, { fehler: boolean }> {
  state = { fehler: false };
  static getDerivedStateFromError() {
    return { fehler: true };
  }
  render() {
    if (!this.state.fehler) return this.props.children;
    return (
      <div className="ls-eingabe ls-komm-fehlt" role="alert">
        <div className="ls-eingabe__kopf">
          <span className="ls-eingabe__titel">Eingabe konnte nicht geladen werden</span>
          <button type="button" className="symbolknopf symbolknopf--klein ls-eingabe__zu" aria-label="Schließen" onClick={this.props.onClose}>
            <Icon name="schliessen" groesse={14} />
          </button>
        </div>
        <p className="ls-leer ls-komm-fehlt__text">Seite neu laden. Hilft das nicht, in Close schreiben.</p>
      </div>
    );
  }
}

export function KommunikationKarte({ art, ...props }: KomposerProps & { art: typeof EMAIL | typeof SMS }) {
  const Komposer = art === EMAIL ? EmailLazy : SmsLazy;
  return (
    <div
      className="ls-komm"
      onKeyDown={(e) => {
        // Esc in der Karte springt nicht zur Liste zurück und schließt nichts nebenbei: ob Esc die Karte schließt
        // (und was mit dem Entwurf passiert), entscheidet der Komposer selbst über onClose.
        if (e.key === "Escape") e.preventDefault();
      }}
    >
      <Grenze key={art} onClose={props.onClose}>
        <Suspense fallback={<div className="ls-eingabe ls-komm-laedt">Wird geladen …</div>}>
          <Komposer {...props} />
        </Suspense>
      </Grenze>
    </div>
  );
}
